import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  type DialogProps,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import type {FC, ReactNode} from "react";

export type AppDialogProps = Omit<DialogProps, "onClose" | "title"> & {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  responsiveFullScreen?: boolean;
};

const AppDialog: FC<AppDialogProps> = props => {
  const {
    open,
    onClose,
    title,
    description,
    children,
    actions,
    responsiveFullScreen = true,
    fullScreen: propFullScreen,
    maxWidth = "sm",
    fullWidth = true,
    ...dialogProps
  } = props;

  const theme = useTheme();
  const responsiveFullScreenValue = useMediaQuery(theme.breakpoints.down("md"));

  // Determine fullscreen mode: prop > responsive > false
  const fullScreen =
    propFullScreen ??
    (responsiveFullScreen ? responsiveFullScreenValue : false);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent closing on backdrop click if requested
        if (reason === "backdropClick") return;
        onClose();
      }}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      {...dialogProps}>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent data-theme={theme.palette.mode}>
        {description && (
          <DialogContentText sx={{mb: 2}}>{description}</DialogContentText>
        )}
        {children}
      </DialogContent>

      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};

export default AppDialog;
